import { initializeApp } from 'firebase/app';
import {
  getAuth, OAuthProvider, signInWithPopup, signInWithRedirect, signOut as fbSignOut,
  onAuthStateChanged, connectAuthEmulator, browserLocalPersistence, setPersistence,
} from 'firebase/auth';
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

const env = import.meta.env;
export const app = initializeApp({
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
});

export const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(() => {});

// Local cache keeps the route on a driver's phone and queues drop-offs when signal drops.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

if (env.VITE_USE_EMULATORS === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

const provider = new OAuthProvider('microsoft.com');
provider.setCustomParameters({ prompt: 'select_account', ...(env.VITE_MS_TENANT_ID ? { tenant: env.VITE_MS_TENANT_ID } : {}) });

export async function signIn() {
  try {
    await signInWithPopup(auth, provider);
  } catch (e) {
    // Popups are blocked in some phone browsers and home screen apps.
    if (['auth/popup-blocked', 'auth/operation-not-supported-in-this-environment', 'auth/cancelled-popup-request'].includes(e.code)) {
      await signInWithRedirect(auth, provider);
    } else throw e;
  }
}
export const signOut = () => fbSignOut(auth);
export const watchAuth = (fn) => onAuthStateChanged(auth, fn);

export function userEmail(user) {
  return (user?.email || user?.providerData?.find((p) => p.email)?.email || '').toLowerCase();
}

export const call = (name) => {
  const fn = httpsCallable(functions, name, { timeout: 300000 });
  return async (data) => (await fn(data)).data;
};
